/**
 * Synthesis — top 3 takeaways on strengths and pain points,
 * nuanced by segment and competitive context, tied to financial performance.
 */

import type { Synthesis as SynthesisType } from "../types/diagnostic";
import { TrendingUp, TrendingDown } from "lucide-react";

interface SynthesisProps {
  synthesis: SynthesisType;
  clientName: string;
}

export function Synthesis({ synthesis }: SynthesisProps) {
  return (
    <div className="mck-card p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-text-primary">
          Diagnostic Synthesis
        </h2>
        <p className="text-sm text-text-secondary mt-1 leading-relaxed max-w-3xl">
          {synthesis.headline}
        </p>
      </div>

      {/* Two-column layout: strengths and risks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Strengths */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <TrendingUp size={16} className="text-green-700" />
            </div>
            <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wide">
              Top Strengths
            </h3>
          </div>
          <div className="space-y-4">
            {synthesis.strengthTakeaways.map((t, idx) => (
              <TakeawayCard
                key={t.id}
                index={idx + 1}
                title={t.title}
                narrative={t.narrative}
                variant="strength"
              />
            ))}
          </div>
        </div>

        {/* Pain points / risks */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
              <TrendingDown size={16} className="text-red-700" />
            </div>
            <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wide">
              Top Risks to NRR
            </h3>
          </div>
          <div className="space-y-4">
            {synthesis.painTakeaways.map((t, idx) => (
              <TakeawayCard
                key={t.id}
                index={idx + 1}
                title={t.title}
                narrative={t.narrative}
                variant="pain"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Individual takeaway card with numbered index and narrative. */
function TakeawayCard({
  index,
  title,
  narrative,
  variant,
}: {
  index: number;
  title: string;
  narrative: string;
  variant: "strength" | "pain";
}) {
  const accentColor =
    variant === "strength"
      ? "border-green-200 bg-green-50/30"
      : "border-red-200 bg-red-50/30";
  const numberColor =
    variant === "strength"
      ? "bg-green-100 text-green-700"
      : "bg-red-100 text-red-700";

  return (
    <div className={`rounded-lg border p-4 ${accentColor}`}>
      <div className="flex items-start gap-3">
        <span
          className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${numberColor}`}
        >
          {index}
        </span>
        <div>
          <p className="text-sm font-medium text-text-primary">{title}</p>
          <p className="text-xs text-text-secondary mt-1.5 leading-relaxed">
            {narrative}
          </p>
        </div>
      </div>
    </div>
  );
}
